import { ScrollArea } from "@/components/ui/scroll-area";
import { useListMessages } from "@/tanstack-hooks";
import Spinner from "../Spinner";
import EmptyState from "../EmptyState";
import { ChevronDownIcon, CloudOffIcon } from "lucide-react";
import { useEffect, useRef, useLayoutEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import SpinnerSmall from "../SpinnerSmall";
import { Button } from "../ui/button";
import { ApiError } from "@/generated-api";
import { useNavigate } from "react-router-dom";
import ChatBubble from "./ChatBubble";
import { useGetAccountLocale } from "@/hooks/useGetAccountLocale";
import { useTranslation } from "@/hooks/useTranslation";

function sectionDate(dateString: string, locale: string = "en"): string {
  const date = new Date(dateString);
  const today = new Date();
  const showYear = date.getFullYear() !== today.getFullYear();

  return date.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    ...(showYear && { year: "numeric" }),
  });
}

function isDifferentDay(timestamp1: string, timestamp2: string): boolean {
  const date1 = new Date(timestamp1);
  const date2 = new Date(timestamp2);

  return (
    date1.getFullYear() !== date2.getFullYear() ||
    date1.getMonth() !== date2.getMonth() ||
    date1.getDate() !== date2.getDate()
  );
}

export default function MessagesList({
  counterpartyId,
  enabled = true,
}: {
  counterpartyId: string;
  enabled?: boolean;
}) {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isScrollComplete, setIsScrollComplete] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const fetchTimeoutRef = useRef<NodeJS.Timeout>();
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const isInitialLoadRef = useRef(true);
  const lastScrollHeightRef = useRef<number>(0);
  const lastScrollTopRef = useRef<number>(0);
  const isManualScrollRef = useRef(false);
  const navigate = useNavigate();
  const locale = useGetAccountLocale();
  const t = useTranslation();

  const { ref, inView } = useInView({ threshold: 0 });

  const {
    data,
    error,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useListMessages(counterpartyId, enabled);

  const getViewport = () =>
    scrollViewportRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]"
    ) as HTMLDivElement;

  // Memoize messages to prevent unnecessary re-renders
  const messages = useMemo(() => {
    if (!data) return [];
    return data.pages
      .slice()
      .reverse()
      .flatMap((page) => [...page.items].reverse());
  }, [data]);

  useEffect(() => {
    if (isScrollComplete && !isInitialLoadComplete) {
      const timer = setTimeout(() => {
        setIsInitialLoadComplete(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isScrollComplete, isInitialLoadComplete]);

  const scrollToBottom = (smooth = false) => {
    const viewport = getViewport();
    if (viewport) {
      isManualScrollRef.current = true;
      viewport.scrollTo({
        top: viewport.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
      // Reset the manual scroll flag after animation
      setTimeout(
        () => {
          isManualScrollRef.current = false;
          setIsScrollComplete(true);
        },
        smooth ? 300 : 0
      );
    }
  };

  // Initial scroll to bottom
  useLayoutEffect(() => {
    const viewport = getViewport();
    if (!viewport) return;

    const checkScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = viewport;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;
      setShowScrollButton(!isNearBottom);
    };

    if (isInitialLoadRef.current && messages.length > 0) {
      const timeoutId = setTimeout(() => {
        scrollToBottom();
        isInitialLoadRef.current = false;
        checkScroll();
      }, 0);
      return () => clearTimeout(timeoutId);
    }

    viewport.addEventListener("scroll", checkScroll);
    return () => viewport.removeEventListener("scroll", checkScroll);
  }, [messages]);

  // Auto-scroll on new messages only if we're at the bottom
  useEffect(() => {
    if (!isInitialLoadRef.current && !isManualScrollRef.current) {
      const viewport = getViewport();
      if (viewport) {
        scrollToBottom(true);
      }
    }
  }, [data?.pages[0]?.items]);

  const storeScrollPosition = () => {
    const viewport = getViewport();
    if (viewport) {
      lastScrollHeightRef.current = viewport.scrollHeight;
      lastScrollTopRef.current = viewport.scrollTop;
    }
  };

  const restoreScrollPosition = () => {
    const viewport = getViewport();
    if (viewport) {
      const newScrollHeight = viewport.scrollHeight;
      const heightDiff = newScrollHeight - lastScrollHeightRef.current;

      // Use RAF for smoother scroll restoration
      requestAnimationFrame(() => {
        if (viewport) {
          viewport.scrollTop = lastScrollTopRef.current + heightDiff;
        }
      });
    }
  };

  // Debounced infinite scroll with pre-fetching
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }

      fetchTimeoutRef.current = setTimeout(() => {
        storeScrollPosition();
        void fetchNextPage().then(() => {
          requestAnimationFrame(restoreScrollPosition);
        });
      }, 400);
    }

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (isError && (error as ApiError)?.status === 404) {
      if (sessionStorage.getItem("chatProfileId") === counterpartyId) {
        sessionStorage.removeItem("chatProfileId");
      }
      navigate("/messages");
    }
  }, [isError, error, counterpartyId, navigate]);

  if (isLoading) {
    return (
      <div className="w-full flex items-center h-[85vh]">
        <Spinner />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="h-[85vh] flex items-center">
        <EmptyState
          icon={CloudOffIcon}
          title={t.MESSAGES.ERRORS.LOADING}
          message={t.MESSAGES.ERRORS.LOADING_DESCRIPTION}
        />
      </div>
    );
  }

  return (
    <>
      <ScrollArea
        className={`h-full px-4 dark ${!isScrollComplete ? "invisible" : ""}`}
        ref={scrollViewportRef}
      >
        <div className="space-y-2 min-h-[85vh] py-3 flex flex-col justify-end">
          {hasNextPage && (
            <div
              ref={ref}
              className="w-full h-5 flex items-center justify-center opacity-50 transition-opacity duration-200"
            >
              {isFetchingNextPage && <SpinnerSmall />}
            </div>
          )}
          {messages.map((message, index) => {
            const prevMessage = index > 0 ? messages[index - 1] : null;
            const showDateSection =
              !prevMessage ||
              (prevMessage &&
                isDifferentDay(message.sent_at, prevMessage.sent_at));

            return (
              <div key={message.message_id}>
                {showDateSection && (
                  <div className="w-full text-center text-sm text-white py-2">
                    {sectionDate(message.sent_at, locale.code)}
                  </div>
                )}
                <ChatBubble
                  counterpartyId={counterpartyId}
                  message={message}
                  scrollViewportRef={scrollViewportRef}
                  isInitialLoad={!isInitialLoadComplete}
                />
              </div>
            );
          })}
        </div>
        {showScrollButton && (
          <Button
            size="icon"
            className="absolute bottom-2 right-4 rounded-full bg-zinc-900 hover:bg-zinc-900 border text-white border-zinc-700"
            onClick={() => scrollToBottom(true)}
          >
            <ChevronDownIcon className="h-6 w-6" />
          </Button>
        )}
      </ScrollArea>
    </>
  );
}
